/**
 * useEAAsyncAnalysis Hook
 * Manages async EA analysis with SignalR real-time progress updates
 *
 * Flow:
 * 1. User clicks analysis button
 * 2. Show consent modal
 * 3. On consent: POST /api/q/ea/analyze/async → get jobId
 * 4. Subscribe to SignalR hub for jobId
 * 5. Receive progress updates (OnProgress, OnComplete, OnError, OnJobCancelled)
 * 6. Display results when complete
 *
 * SignalR Hub: ws://localhost:5100/hubs/ea-progress (DataFeed direct)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { subscribeToJob, unsubscribeFromJob, disconnect } from '../SignalR/eaProgressHub';
import {
  startAsyncAnalysis,
  getJobStatus,
  cancelJob,
  checkEAHash,
  getAnalysisById,
  getMQLLibrary,
  loadLibraryAnalysis
} from '../api/eaAnalysisApi';
import { getEAContent, validateEAFileSize } from '../api/qbridgeEaApi';
import { computeEAContentHash } from '../utils/hashUtils';

/**
 * Analysis states
 */
export const ANALYSIS_STATE = {
  IDLE: 'idle',
  LOADING_CONTENT: 'loading_content',
  AWAITING_CONSENT: 'awaiting_consent',
  SUBMITTING: 'submitting',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

/**
 * Progress stages with descriptions
 */
export const PROGRESS_STAGES = {
  'queued': { label: 'Queued', description: 'Waiting in analysis queue...' },
  'starting': { label: 'Starting', description: 'Initializing analysis...' },
  'parsing': { label: 'Parsing', description: 'Reading source code...' },
  'analyzing': { label: 'Analyzing', description: 'Analyzing trading logic...' },
  'bug_detection': { label: 'Bug Detection', description: 'Checking for bugs and issues...' },
  'quality_check': { label: 'Quality Check', description: 'Evaluating code quality...' },
  'performance': { label: 'Performance', description: 'Analyzing performance...' },
  'risk_analysis': { label: 'Risk Analysis', description: 'Assessing risk factors...' },
  'generating': { label: 'Generating', description: 'Generating report...' },
  'completing': { label: 'Completing', description: 'Finalizing results...' }
};

/**
 * useEAAsyncAnalysis Hook
 * @param {string} token - JWT authentication token
 * @returns {object} Analysis state and handlers
 */
const useEAAsyncAnalysis = (token) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════════

  // Analysis state
  const [state, setState] = useState(ANALYSIS_STATE.IDLE);
  const [error, setError] = useState(null);

  // Job tracking
  const [jobId, setJobId] = useState(null);
  const [queuePosition, setQueuePosition] = useState(null);

  // Progress tracking
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(null);
  const [chunksCompleted, setChunksCompleted] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);

  // Results
  const [result, setResult] = useState(null);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [processingTimeMs, setProcessingTimeMs] = useState(0);

  // File content cache
  const [fileContent, setFileContent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Consent modal
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [analysisType, setAnalysisType] = useState('full');

  // P9-B: Duplicate detection state
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [isCheckingHash, setIsCheckingHash] = useState(false);

  // P9-C: MQL Library state
  const [libraryItems, setLibraryItems] = useState([]);
  const [libraryGrouped, setLibraryGrouped] = useState({});
  const [libraryByType, setLibraryByType] = useState({});
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState(null);

  // Refs for cleanup
  const currentJobIdRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════════════════

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentJobIdRef.current) {
        unsubscribeFromJob(currentJobIdRef.current);
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // SIGNALR HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Handle progress update from SignalR
   */
  const handleProgress = useCallback((progressData) => {
    console.log('[useEAAsyncAnalysis] Progress update:', progressData);

    // Support both object and number progress
    if (typeof progressData === 'object') {
      setProgress(progressData.progress || 0);
      setStage(progressData.stage || null);
      setChunksCompleted(progressData.chunksCompleted || 0);
      setTotalChunks(progressData.totalChunks || 0);
    } else {
      setProgress(progressData);
    }

    setState(ANALYSIS_STATE.PROCESSING);
  }, []);

  /**
   * Handle completion from SignalR or polling
   * GUARD: Prevent double-completion from race between SignalR and polling
   */
  const handleComplete = useCallback((resultData) => {
    // Guard: If already completed, ignore duplicate completion
    if (state === ANALYSIS_STATE.COMPLETED) {
      console.log('[useEAAsyncAnalysis] Ignoring duplicate completion - already in COMPLETED state');
      return;
    }

    console.log('[useEAAsyncAnalysis] Analysis complete - raw resultData:', JSON.stringify(resultData, null, 2));

    // FIRST: Stop polling to prevent race conditions
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      console.log('[useEAAsyncAnalysis] Stopped polling on completion');
    }

    setProgress(100);
    // Extract the actual result - handle nested structures
    const extractedResult = resultData.result || resultData;
    console.log('[useEAAsyncAnalysis] Extracted result:', JSON.stringify(extractedResult, null, 2));
    console.log('[useEAAsyncAnalysis] Result keys:', Object.keys(extractedResult || {}));

    setResult(extractedResult);
    setTokensUsed(resultData.tokensUsed || 0);
    setProcessingTimeMs(resultData.processingTimeMs || 0);
    setState(ANALYSIS_STATE.COMPLETED);

    // Cleanup SignalR subscription
    if (currentJobIdRef.current) {
      unsubscribeFromJob(currentJobIdRef.current);
      currentJobIdRef.current = null;
    }
  }, [state]);

  /**
   * Handle error from SignalR
   */
  const handleError = useCallback((errorData) => {
    console.error('[useEAAsyncAnalysis] Analysis error:', errorData);

    setError({
      message: typeof errorData === 'string' ? errorData : errorData.error || 'Analysis failed',
      code: errorData.errorCode,
      retryable: errorData.retryable ?? true
    });
    setState(ANALYSIS_STATE.FAILED);

    // Cleanup
    if (currentJobIdRef.current) {
      unsubscribeFromJob(currentJobIdRef.current);
      currentJobIdRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /**
   * Handle cancellation from SignalR
   */
  const handleCancelled = useCallback(() => {
    console.log('[useEAAsyncAnalysis] Job cancelled');

    setState(ANALYSIS_STATE.CANCELLED);
    setError({ message: 'Analysis was cancelled', retryable: true });

    // Cleanup
    if (currentJobIdRef.current) {
      unsubscribeFromJob(currentJobIdRef.current);
      currentJobIdRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // POLLING FALLBACK
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Start polling for job status (fallback if SignalR fails)
   */
  const startPolling = useCallback((jobIdToPolL) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    console.log('[useEAAsyncAnalysis] Starting polling for jobId:', jobIdToPolL);

    const poll = async () => {
      try {
        console.log('[useEAAsyncAnalysis] Polling job status...');
        const statusResult = await getJobStatus(jobIdToPolL);
        console.log('[useEAAsyncAnalysis] Poll result:', JSON.stringify(statusResult, null, 2));

        if (statusResult.success && statusResult.data) {
          const data = statusResult.data;
          // Normalize status to lowercase (backend may return PascalCase)
          const status = (data.status || data.Status || '').toLowerCase();
          const jobProgress = data.progress ?? data.Progress;
          const jobResult = data.result || data.Result;

          // DEBUG: Log what we received
          console.log('[useEAAsyncAnalysis] Poll extracted:', {
            rawStatus: data.status || data.Status,
            normalizedStatus: status,
            progress: jobProgress,
            hasResult: !!jobResult,
            resultKeys: jobResult ? Object.keys(jobResult) : [],
            allDataKeys: Object.keys(data)
          });

          // Backend may return 'complete' or 'completed' - handle both
          if (status === 'completed' || status === 'complete') {
            // Handle completed status - even if result is empty, we should transition
            if (jobResult && Object.keys(jobResult).length > 0) {
              // Wrap result in expected format for handleComplete
              handleComplete({
                result: jobResult,
                tokensUsed: data.tokensUsed ?? data.TokensUsed ?? 0,
                processingTimeMs: data.processingTimeMs ?? data.ProcessingTimeMs ?? 0
              });
            } else {
              // Backend says completed but no result - this is an error condition
              console.error('[useEAAsyncAnalysis] Backend returned completed but no result!');
              handleError('Analysis completed but no result returned. Please try again.');
            }
          } else if (status === 'failed') {
            handleError(statusResult.data.error || 'Analysis failed');
          } else if (status === 'cancelled') {
            handleCancelled();
          } else {
            // Still processing
            if (jobProgress !== undefined) {
              setProgress(jobProgress);
            }
            // Also extract chunk info if available (handle both camelCase and PascalCase)
            const chunksComp = data.chunksCompleted ?? data.ChunksCompleted;
            const totalCh = data.totalChunks ?? data.TotalChunks;
            if (chunksComp !== undefined) {
              setChunksCompleted(chunksComp);
            }
            if (totalCh !== undefined) {
              setTotalChunks(totalCh);
            }
            setState(status === 'queued' ? ANALYSIS_STATE.QUEUED : ANALYSIS_STATE.PROCESSING);
          }
        } else {
          console.warn('[useEAAsyncAnalysis] Poll returned no data:', statusResult);
        }
      } catch (error) {
        console.error('[useEAAsyncAnalysis] Polling error:', error);
      }
    };

    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(poll, 2000);

    // Initial poll
    poll();
  }, [handleComplete, handleError, handleCancelled]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ANALYSIS FLOW
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Request analysis for a file (opens consent modal)
   * Now includes P9-B hash checking for duplicates
   * @param {object} file - EA file to analyze
   * @param {string} type - Analysis type ('bugs' | 'quality' | 'performance' | 'risk' | 'full')
   * @param {string} userId - User ID for hash check
   */
  const requestAnalysis = useCallback(async (file, type = 'full', userId = null) => {
    if (!file || !token) return;

    setSelectedFile(file);
    setAnalysisType(type);
    setError(null);
    setDuplicateInfo(null);
    setShowDuplicateModal(false);
    setState(ANALYSIS_STATE.LOADING_CONTENT);

    try {
      // Load file content first
      const contentResult = await getEAContent(token, file.name, true);

      if (!contentResult.success) {
        setError({ message: contentResult.error || 'Failed to load file content' });
        setState(ANALYSIS_STATE.FAILED);
        return;
      }

      // Validate file size
      const sizeValidation = validateEAFileSize(contentResult.main_source);
      if (!sizeValidation.valid) {
        setError({ message: sizeValidation.error });
        setState(ANALYSIS_STATE.FAILED);
        return;
      }

      setFileContent(contentResult);

      // P9-B: Check for duplicate via hash (if userId provided)
      if (userId) {
        setIsCheckingHash(true);
        try {
          const hash = await computeEAContentHash(contentResult);
          console.log('[useEAAsyncAnalysis] Computed hash:', hash);

          const hashResult = await checkEAHash(userId, hash);
          console.log('[useEAAsyncAnalysis] Hash check result:', hashResult);

          if (hashResult.success && hashResult.data?.exists) {
            // Duplicate found - show modal with options
            setDuplicateInfo(hashResult.data);
            setShowDuplicateModal(true);
            setIsCheckingHash(false);
            setState(ANALYSIS_STATE.IDLE); // Wait for user choice
            return;
          }
        } catch (hashError) {
          console.warn('[useEAAsyncAnalysis] Hash check failed, proceeding:', hashError);
          // Non-blocking - continue to consent if hash check fails
        }
        setIsCheckingHash(false);
      }

      // No duplicate - proceed to consent modal
      setShowConsentModal(true);
      setState(ANALYSIS_STATE.AWAITING_CONSENT);
    } catch (error) {
      console.error('[useEAAsyncAnalysis] Failed to load content:', error);
      setError({ message: error.message || 'Failed to load file' });
      setState(ANALYSIS_STATE.FAILED);
    }
  }, [token]);

  /**
   * Start analysis after consent is given
   * @param {boolean} consentGiven - Must be true to proceed
   */
  const startAnalysisWithConsent = useCallback(async (consentGiven) => {
    if (!consentGiven || !selectedFile || !fileContent || !token) {
      setError({ message: 'Consent is required to analyze code' });
      return;
    }

    setShowConsentModal(false);
    setState(ANALYSIS_STATE.SUBMITTING);
    setProgress(0);
    setStage(null);
    setResult(null);
    setError(null);

    try {
      // Validate source content
      if (!fileContent.main_source || fileContent.main_source.trim().length === 0) {
        setError({ message: 'EA source code is empty or missing. Please reload the file.' });
        setState(ANALYSIS_STATE.FAILED);
        return;
      }

      console.log('[useEAAsyncAnalysis] Starting analysis with:', {
        fileName: selectedFile.name,
        mainSourceLength: fileContent.main_source?.length || 0,
        includesCount: fileContent.includes ? Object.keys(fileContent.includes).length : 0
      });

      // Start async analysis with JSON payload (NOT File object)
      const response = await startAsyncAnalysis({
        name: selectedFile.name,
        mainSource: fileContent.main_source,
        includeFiles: fileContent.includes || {},
        consentGiven: true
      });

      if (!response.success) {
        setError({ message: response.error || 'Failed to start analysis' });
        setState(ANALYSIS_STATE.FAILED);
        return;
      }

      // DEBUG: Log full response to diagnose FIX Engine proxy issue
      console.log('[useEAAsyncAnalysis] API Response:', JSON.stringify(response.data, null, 2));

      // Check if result was returned immediately (cache hit)
      // Backend returns { jobId, result, status: 'completed', tokensUsed, processingTimeMs } on cache hit
      // Also check for 'result' directly in case 'status' field is missing from FIX Engine response
      if ((response.data.status === 'completed' && response.data.result) ||
          (response.data.result && typeof response.data.result === 'object')) {
        console.log('[useEAAsyncAnalysis] Cache hit - immediate result received');
        handleComplete({
          result: response.data.result,
          tokensUsed: response.data.tokensUsed || 0,
          processingTimeMs: response.data.processingTimeMs || 0
        });
        return;
      }

      const newJobId = response.data.jobId;
      setJobId(newJobId);
      setQueuePosition(response.data.queuePosition || 0);
      currentJobIdRef.current = newJobId;

      setState(response.data.queuePosition > 0 ? ANALYSIS_STATE.QUEUED : ANALYSIS_STATE.PROCESSING);

      // Subscribe to SignalR updates
      console.log('[useEAAsyncAnalysis] Attempting SignalR subscription for jobId:', newJobId);
      const subscribed = await subscribeToJob(newJobId, {
        onProgress: handleProgress,
        onComplete: handleComplete,
        onError: handleError,
        onCancelled: handleCancelled
      });

      console.log('[useEAAsyncAnalysis] SignalR subscription result:', subscribed);

      if (!subscribed) {
        console.warn('[useEAAsyncAnalysis] SignalR subscription failed, falling back to polling');
        startPolling(newJobId);
      } else {
        // Also start polling as backup in case SignalR messages don't arrive
        console.log('[useEAAsyncAnalysis] Starting backup polling alongside SignalR');
        startPolling(newJobId);
      }
    } catch (error) {
      console.error('[useEAAsyncAnalysis] Failed to start analysis:', error);
      setError({ message: error.message || 'Failed to start analysis' });
      setState(ANALYSIS_STATE.FAILED);
    }
  }, [selectedFile, fileContent, token, handleProgress, handleComplete, handleError, handleCancelled, startPolling]);

  /**
   * Cancel ongoing analysis
   */
  const cancelAnalysis = useCallback(async () => {
    if (!jobId) return;

    try {
      await cancelJob(jobId);
      handleCancelled();
    } catch (error) {
      console.error('[useEAAsyncAnalysis] Failed to cancel:', error);
    }
  }, [jobId, handleCancelled]);

  /**
   * Reset state to start fresh
   */
  const reset = useCallback(() => {
    // Cleanup
    if (currentJobIdRef.current) {
      unsubscribeFromJob(currentJobIdRef.current);
      currentJobIdRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Reset all state
    setState(ANALYSIS_STATE.IDLE);
    setError(null);
    setJobId(null);
    setQueuePosition(null);
    setProgress(0);
    setStage(null);
    setChunksCompleted(0);
    setTotalChunks(0);
    setResult(null);
    setTokensUsed(0);
    setProcessingTimeMs(0);
    setFileContent(null);
    setSelectedFile(null);
    setShowConsentModal(false);
  }, []);

  /**
   * Close consent modal without starting analysis
   */
  const closeConsentModal = useCallback(() => {
    setShowConsentModal(false);
    setState(ANALYSIS_STATE.IDLE);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // P9-B: DUPLICATE HANDLING
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Close duplicate modal without action
   */
  const closeDuplicateModal = useCallback(() => {
    setShowDuplicateModal(false);
    setDuplicateInfo(null);
    setState(ANALYSIS_STATE.IDLE);
  }, []);

  /**
   * Use existing analysis from duplicate detection
   * Loads the analysis result instantly (0 tokens)
   */
  const useExistingAnalysis = useCallback(async () => {
    if (!duplicateInfo?.analysisId) {
      setError({ message: 'No existing analysis ID found' });
      return;
    }

    setState(ANALYSIS_STATE.LOADING_CONTENT);
    setShowDuplicateModal(false);

    try {
      const result = await getAnalysisById(duplicateInfo.analysisId);

      if (!result.success) {
        setError({ message: result.error || 'Failed to load existing analysis' });
        setState(ANALYSIS_STATE.FAILED);
        return;
      }

      // Set result as if analysis just completed
      setProgress(100);
      setResult(result.data.result || result.data);
      setTokensUsed(0); // No tokens used - loaded from cache
      setProcessingTimeMs(0);
      setState(ANALYSIS_STATE.COMPLETED);
      setDuplicateInfo(null);

      console.log('[useEAAsyncAnalysis] Loaded existing analysis:', result.data);
    } catch (error) {
      console.error('[useEAAsyncAnalysis] Failed to load existing analysis:', error);
      setError({ message: error.message || 'Failed to load analysis' });
      setState(ANALYSIS_STATE.FAILED);
    }
  }, [duplicateInfo]);

  /**
   * Proceed with re-analysis despite duplicate
   * Opens consent modal to continue normal flow
   */
  const proceedWithReanalysis = useCallback(() => {
    setShowDuplicateModal(false);
    setDuplicateInfo(null);
    setShowConsentModal(true);
    setState(ANALYSIS_STATE.AWAITING_CONSENT);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // P9-C: MQL LIBRARY
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Load user's MQL library
   * @param {string} userId - User ID
   * @param {object} options - Filter options
   */
  const loadLibrary = useCallback(async (userId, options = {}) => {
    if (!userId) return;

    setIsLoadingLibrary(true);
    setLibraryError(null);

    try {
      const result = await getMQLLibrary(userId, options);

      if (!result.success) {
        setLibraryError(result.error || 'Failed to load library');
        setLibraryItems([]);
        setLibraryGrouped({});
        setLibraryByType({});
      } else {
        setLibraryItems(result.data || []);
        setLibraryGrouped(result.grouped || {});
        setLibraryByType(result.byType || {});
      }
    } catch (error) {
      console.error('[useEAAsyncAnalysis] Failed to load library:', error);
      setLibraryError(error.message || 'Failed to load library');
    } finally {
      setIsLoadingLibrary(false);
    }
  }, []);

  /**
   * Load analysis from library item (instant, 0 tokens)
   * @param {object} libraryItem - Item from MQL library
   */
  const loadFromLibrary = useCallback(async (libraryItem) => {
    if (!libraryItem?.latestAnalysisId) {
      setError({ message: 'No analysis available for this file' });
      return;
    }

    setState(ANALYSIS_STATE.LOADING_CONTENT);
    setError(null);

    // Create a file-like object for display
    setSelectedFile({
      name: libraryItem.name,
      file_type: libraryItem.fileType?.toLowerCase() || 'expert',
      line_count: libraryItem.lineCount
    });

    try {
      const result = await loadLibraryAnalysis(libraryItem.latestAnalysisId);

      if (!result.success) {
        setError({ message: result.error || 'Failed to load analysis from library' });
        setState(ANALYSIS_STATE.FAILED);
        return;
      }

      // Set result as completed
      setProgress(100);
      setResult(result.data.result || result.data);
      setTokensUsed(0); // No tokens used - loaded from library
      setProcessingTimeMs(0);
      setState(ANALYSIS_STATE.COMPLETED);

      console.log('[useEAAsyncAnalysis] Loaded from library:', libraryItem.name);
    } catch (error) {
      console.error('[useEAAsyncAnalysis] Failed to load from library:', error);
      setError({ message: error.message || 'Failed to load analysis' });
      setState(ANALYSIS_STATE.FAILED);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // DERIVED STATE
  // ═══════════════════════════════════════════════════════════════════════════

  const isAnalyzing = [
    ANALYSIS_STATE.LOADING_CONTENT,
    ANALYSIS_STATE.SUBMITTING,
    ANALYSIS_STATE.QUEUED,
    ANALYSIS_STATE.PROCESSING
  ].includes(state);

  const isComplete = state === ANALYSIS_STATE.COMPLETED;
  const isFailed = state === ANALYSIS_STATE.FAILED;
  const isCancelled = state === ANALYSIS_STATE.CANCELLED;

  const stageInfo = stage ? PROGRESS_STAGES[stage] || { label: stage, description: 'Processing...' } : null;

  // ═══════════════════════════════════════════════════════════════════════════
  // RETURN
  // ═══════════════════════════════════════════════════════════════════════════

  return {
    // State
    state,
    isAnalyzing,
    isComplete,
    isFailed,
    isCancelled,
    error,

    // Job info
    jobId,
    queuePosition,

    // Progress
    progress,
    stage,
    stageInfo,
    chunksCompleted,
    totalChunks,

    // Results
    result,
    tokensUsed,
    processingTimeMs,

    // File
    selectedFile,
    fileContent,

    // Consent modal
    showConsentModal,
    analysisType,
    closeConsentModal,

    // P9-B: Duplicate detection
    showDuplicateModal,
    duplicateInfo,
    isCheckingHash,
    closeDuplicateModal,
    useExistingAnalysis,
    proceedWithReanalysis,

    // P9-C: MQL Library
    libraryItems,
    libraryGrouped,
    libraryByType,
    isLoadingLibrary,
    libraryError,
    loadLibrary,
    loadFromLibrary,

    // Actions
    requestAnalysis,
    startAnalysisWithConsent,
    cancelAnalysis,
    reset
  };
};

export default useEAAsyncAnalysis;
